import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import currencyFlags from "../../Data/CurrencyFlags.json";
import SettingsTabs from "../AllTabs/SettingsTabs";
import "./Supplier.css";

const Supplier = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [apiShow, setAPIShow] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // State for multiple suppliers in API modal
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [selectedSupplierType, setSelectedSupplierType] = useState("FLIGHT");

  // State for API form - now handling single supplier
  const [supplierForm, setSupplierForm] = useState({});
  const [errors, setErrors] = useState({});
  const [btnLoading, setBtnLoading] = useState(false);

  // Supplier types
  const supplierTypes = [
    "FLIGHT",
    "HOTEL",
    "PACKAGES",
    "BUS",
    "TRAINS",
    "CAR",
    "TRANSFER",
    "SIGHTSEEING",
    "VISA",
  ];

  const initializeSupplierForm = (supplierType, existingData = null) => {
    if (existingData && existingData.supplierId) {
      return {
        supplierId: existingData.supplierId,
        supplierCompanyId: selectedCompany?.companyId || "",
        isActive:
          existingData.isActive !== undefined ? existingData.isActive : true,
        apiType: existingData.apiType || "DEVELOPMENT",
        devAgentCode:
          existingData.devAgentCode || existingData.DEVAgentCode || "",
        devapiurl: existingData.devapiurl || existingData.DEVAPIURL || "",
        devapikey: existingData.devapikey || existingData.DEVAPIKEY || "",
        devusername: existingData.devusername || existingData.DEVUSERNAME || "",
        devpassword: existingData.devpassword || existingData.DEVPASSWORD || "",
        prodAgentCode:
          existingData.prodAgentCode || existingData.PRODAgentCode || "",
        prodapiurl: existingData.prodapiurl || existingData.PRODAPIURL || "",
        prodapikey: existingData.prodapikey || existingData.PRODAPIKEY || "",
        produsername:
          existingData.produsername || existingData.PRODUSERNAME || "",
        prodpassword:
          existingData.prodpassword || existingData.PRODPASSWORD || "",
        type: supplierType,
        companyId: user?.cid || "",
      };
    }

    return {
      supplierId: "00000000-0000-0000-0000-000000000000",
      supplierCompanyId: selectedCompany?.companyId || "",
      isActive: true,
      apiType: "DEVELOPMENT",
      devAgentCode: "",
      devapiurl: "",
      devapikey: "",
      devusername: "",
      devpassword: "",
      prodAgentCode: "",
      prodapiurl: "",
      prodapikey: "",
      produsername: "",
      prodpassword: "",
      type: supplierType,
      companyId: user?.cid || "",
    };
  };

  // Fetch companies function
  const GetSupplierCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const response = await apiService.getWithParams(
        `${config.GETCOMPANIES}?companyType=SUPPLIER`
      );

      if (response && response.data) {
        setCompanies(response.data);
        setFilteredCompanies(response.data);
      }
    } catch (error) {
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchSuppliers = async (suppliercompanyId) => {
    setLoadingSuppliers(true);
    try {
      const response = await apiService.getSingle(
        config.GETAPIKEYS +
          "?SupplierCompanyId=" +
          suppliercompanyId +
          "&CompanyId=" +
          user?.cid
      );

      let suppliersData = [];

      if (Array.isArray(response)) {
        suppliersData = response;
      } else if (response && Array.isArray(response.data)) {
        suppliersData = response.data;
      } else {
        suppliersData = [];
      }

      setSuppliers(suppliersData);

      // Initialize form for selected supplier type
      const existingSupplier = suppliersData.find(
        (s) => s.type === selectedSupplierType
      );
      setSupplierForm(
        initializeSupplierForm(selectedSupplierType, existingSupplier)
      );
    } catch (error) {
      // Initialize empty form if no suppliers found
      setSupplierForm(initializeSupplierForm(selectedSupplierType));
      setSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  // Check if a supplier type has data
  const hasSupplierData = (supplierType) => {
    if (!suppliers || !Array.isArray(suppliers) || suppliers.length === 0) {
      return false;
    }

    const supplier = suppliers.find((s) => s.type === supplierType);
    const hasData = !!supplier;
    return hasData;
  };

  // Generate search suggestions
  const generateSuggestions = (searchValue) => {
    if (!searchValue.trim()) {
      setSuggestions([]);
      return;
    }

    const searchLower = searchValue.toLowerCase();
    const companyMatches = companies
      .filter((company) =>
        company.companyName?.toLowerCase().includes(searchLower)
      )
      .slice(0, 5);

    const brandMatches = companies
      .filter(
        (company) =>
          company.brandName?.toLowerCase().includes(searchLower) &&
          !company.companyName?.toLowerCase().includes(searchLower)
      )
      .slice(0, 3);

    const emailMatches = companies
      .filter(
        (company) =>
          company.email?.toLowerCase().includes(searchLower) &&
          !company.companyName?.toLowerCase().includes(searchLower) &&
          !company.brandName?.toLowerCase().includes(searchLower)
      )
      .slice(0, 2);

    const allSuggestions = [
      ...companyMatches,
      ...brandMatches,
      ...emailMatches,
    ];
    setSuggestions(allSuggestions);
  };

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Generate suggestions
    generateSuggestions(value);
    setShowSuggestions(true);

    if (value.trim() === "") {
      setFilteredCompanies(companies);
      setSuggestions([]);
      setShowSuggestions(false);
    } else {
      const filtered = companies.filter(
        (company) =>
          company.companyName?.toLowerCase().includes(value.toLowerCase()) ||
          company.brandName?.toLowerCase().includes(value.toLowerCase()) ||
          company.email?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCompanies(filtered);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (company) => {
    setSearchTerm(company.companyName);
    setFilteredCompanies([company]);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Handle reset
  const handleReset = () => {
    setSearchTerm("");
    setFilteredCompanies(companies);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Handle input blur (hide suggestions after a delay)
  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Handle Key icon click (for companies with isSupplierAPI: true)
  const handleKeyClick = async (company) => {
    setSelectedCompany(company);
    setAPIShow(true);
    await fetchSuppliers(company.companyId);
  };

  // Close API modal
  const handleAPIClose = () => {
    setAPIShow(false);
    setSelectedCompany(null);
    setSuppliers([]);
    setSupplierForm({});
    setSelectedSupplierType("FLIGHT");
  };

  // Handle supplier type change
  const handleSupplierTypeChange = async (type) => {
    setSelectedSupplierType(type);

    // Find existing supplier data for the selected type
    const existingSupplier = suppliers.find((s) => s.type === type);
    setSupplierForm(initializeSupplierForm(type, existingSupplier));
  };

  // Handle input change for supplier form
  const handleSupplierInputChange = (field, value) => {
    setSupplierForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBtnLoading(true);

    try {
      const submitData = {
        supplierId: supplierForm.supplierId,
        supplierCompanyId: selectedCompany?.companyId,
        isActive: supplierForm.isActive,
        apiType: supplierForm.apiType,
        DEVAgentCode: supplierForm.devAgentCode,
        DEVAPIURL: supplierForm.devapiurl,
        DEVAPIKEY: supplierForm.devapikey,
        DEVUSERNAME: supplierForm.devusername,
        DEVPASSWORD: supplierForm.devpassword,
        PRODAgentCode: supplierForm.prodAgentCode,
        PRODAPIURL: supplierForm.prodapiurl,
        PRODAPIKEY: supplierForm.prodapikey,
        PRODUSERNAME: supplierForm.produsername,
        PRODPASSWORD: supplierForm.prodpassword,
        type: supplierForm.type,
        companyId: user?.cid,
      };

      const isNew =
        submitData.supplierId === "00000000-0000-0000-0000-000000000000";

      try {
        if (isNew) {
          await apiService.create(config.CREATESUPPLIER, submitData);
        } else {
          await apiService.update(config.UPDATESUPPLIER, submitData);
        }

        handleAPIClose();
      } catch (error) {}
    } finally {
      setBtnLoading(false);
    }
  };

  const renderSupplierForm = () => {
    return (
      <div className="row">
        {/* Supplier Type */}
        <div className="col-lg-3 mb-3">
          <label className="form-label">Supplier Type</label>
          <select
            value={selectedSupplierType}
            onChange={(e) => handleSupplierTypeChange(e.target.value)}
            className="form-select"
          >
            {supplierTypes.map((type) => (
              <option key={type} value={type}>
                {type} {hasSupplierData(type) && "✓"}
              </option>
            ))}
          </select>
        </div>

        {/* API Type */}
        <div className="col-lg-3 mb-3">
          <label className="form-label">API Type</label>
          <select
            value={supplierForm.apiType || "DEVELOPMENT"}
            onChange={(e) =>
              handleSupplierInputChange("apiType", e.target.value)
            }
            className="form-select"
          >
            <option value="DEVELOPMENT">DEVELOPMENT</option>
            <option value="PRODUCTION">PRODUCTION</option>
          </select>
        </div>

        {/* Status */}
        <div className="col-lg-3 mb-3">
          <label className="form-label">Status</label>
          <select
            value={
              supplierForm.isActive !== undefined ? supplierForm.isActive : true
            }
            onChange={(e) =>
              handleSupplierInputChange("isActive", e.target.value === "true")
            }
            className="form-select"
          >
            <option value={true}>Active</option>
            <option value={false}>Inactive</option>
          </select>
        </div>

        <div className="col-lg-3 mb-3">
          <label className="form-label">&nbsp;</label>
          <div className="form-text text-center">
            {hasSupplierData(selectedSupplierType) ? (
              <span className="badge bg-success">API Configured ✓</span>
            ) : (
              <span className="badge bg-warning">No API Configured</span>
            )}
          </div>
        </div>

        {/* Development API Configuration */}
        <div className="col-12 mb-3">
          <h6 className="border-bottom pb-2">Development API Configuration</h6>
        </div>

        <div className="col-lg-3 mb-3">
          <label className="form-label">DEV Agent Code</label>
          <input
            type="text"
            value={supplierForm.devAgentCode || ""}
            onChange={(e) =>
              handleSupplierInputChange("devAgentCode", e.target.value)
            }
            className="form-control"
            placeholder="Please enter development agent code"
          />
        </div>

        <div className="col-lg-6 mb-3">
          <label className="form-label">DEV API URL</label>
          <input
            type="text"
            value={supplierForm.devapiurl || ""}
            onChange={(e) =>
              handleSupplierInputChange("devapiurl", e.target.value)
            }
            className="form-control"
            placeholder="Please enter development API URL"
          />
        </div>

        <div className="col-lg-3 mb-3">
          <label className="form-label">DEV API KEY</label>
          <input
            type="text"
            value={supplierForm.devapikey || ""}
            onChange={(e) =>
              handleSupplierInputChange("devapikey", e.target.value)
            }
            className="form-control"
            placeholder="Please enter development API key"
          />
        </div>

        <div className="col-lg-3 mb-3">
          <label className="form-label">DEV API USERNAME</label>
          <input
            type="text"
            value={supplierForm.devusername || ""}
            onChange={(e) =>
              handleSupplierInputChange("devusername", e.target.value)
            }
            className="form-control"
            placeholder="Please enter development username"
          />
        </div>

        <div className="col-lg-3 mb-3">
          <label className="form-label">DEV API PASSWORD</label>
          <input
            type="password"
            value={supplierForm.devpassword || ""}
            onChange={(e) =>
              handleSupplierInputChange("devpassword", e.target.value)
            }
            className="form-control"
            placeholder="Please enter development password"
          />
        </div>

        {/* Production API Configuration */}
        <div className="col-12 mb-3">
          <h6 className="border-bottom pb-2">Production API Configuration</h6>
        </div>

        <div className="col-lg-3 mb-3">
          <label className="form-label">PROD Agent Code</label>
          <input
            type="text"
            value={supplierForm.prodAgentCode || ""}
            onChange={(e) =>
              handleSupplierInputChange("prodAgentCode", e.target.value)
            }
            className="form-control"
            placeholder="Please enter production agent code"
          />
        </div>

        <div className="col-lg-6 mb-3">
          <label className="form-label">PROD API URL</label>
          <input
            type="text"
            value={supplierForm.prodapiurl || ""}
            onChange={(e) =>
              handleSupplierInputChange("prodapiurl", e.target.value)
            }
            className="form-control"
            placeholder="Please enter production API URL"
          />
        </div>

        <div className="col-lg-3 mb-3">
          <label className="form-label">PROD API KEY</label>
          <input
            type="text"
            value={supplierForm.prodapikey || ""}
            onChange={(e) =>
              handleSupplierInputChange("prodapikey", e.target.value)
            }
            className="form-control"
            placeholder="Please enter production API key"
          />
        </div>

        <div className="col-lg-3 mb-3">
          <label className="form-label">PROD API USERNAME</label>
          <input
            type="text"
            value={supplierForm.produsername || ""}
            onChange={(e) =>
              handleSupplierInputChange("produsername", e.target.value)
            }
            className="form-control"
            placeholder="Please enter production username"
          />
        </div>

        <div className="col-lg-3 mb-3">
          <label className="form-label">PROD API PASSWORD</label>
          <input
            type="password"
            value={supplierForm.prodpassword || ""}
            onChange={(e) =>
              handleSupplierInputChange("prodpassword", e.target.value)
            }
            className="form-control"
            placeholder="Please enter production password"
          />
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (user && !loading) {
      GetSupplierCompanies();
    }
  }, [user, loading]);

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="p-2">
                    <SettingsTabs pageName="SUPPLIER" />
                  </div>
                  <div className="card-body">
                    {/* Search Section */}
                    <div className="row mb-4">
                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5 className="card-title mb-0">
                            Supplier Companies
                          </h5>
                        </div>
                        <div className="supplier-search-reset-container">
                          <div className="supplier-search-wrapper">
                            <div className="supplier-search-input-group">
                              <i className="ri-search-line supplier-search-icon"></i>
                              <input
                                type="text"
                                className="supplier-search-input"
                                placeholder="Search by company name, brand name, or email..."
                                value={searchTerm}
                                onChange={handleSearch}
                                onFocus={() => setShowSuggestions(true)}
                                onBlur={handleInputBlur}
                              />
                              {searchTerm && (
                                <button
                                  className="supplier-search-clear"
                                  onClick={() => setSearchTerm("")}
                                >
                                  <i className="ri-close-line"></i>
                                </button>
                              )}

                              {/* Search Suggestions Dropdown */}
                              {showSuggestions && suggestions.length > 0 && (
                                <div className="supplier-suggestions-dropdown">
                                  {suggestions.map((company, index) => (
                                    <div
                                      key={company.companyId}
                                      className="supplier-suggestion-item"
                                      onClick={() =>
                                        handleSuggestionClick(company)
                                      }
                                    >
                                      <div className="supplier-suggestion-main">
                                        <i className="ri-building-2-line supplier-suggestion-icon"></i>
                                        <div className="supplier-suggestion-details">
                                          <div className="supplier-suggestion-name">
                                            {company.companyName}
                                          </div>
                                          {company.brandName &&
                                            company.brandName !==
                                              company.companyName && (
                                              <div className="supplier-suggestion-brand">
                                                {company.brandName}
                                              </div>
                                            )}
                                          {company.email && (
                                            <div className="supplier-suggestion-email">
                                              {company.email}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <button
                              className="supplier-reset-btn"
                              onClick={handleReset}
                            >
                              <i className="ri-refresh-line me-1"></i>
                              Reset
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Companies Grid - 4 cards per row with custom grid */}
                    <div
                      className="row supplier-cards-container"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: "1.5rem",
                        padding: "1rem 0",
                      }}
                    >
                      {loadingCompanies ? (
                        <div className="col-12 text-center">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mt-2">Loading supplier companies...</p>
                        </div>
                      ) : filteredCompanies.length > 0 ? (
                        filteredCompanies.map((company, index) => (
                          <div
                            key={company.companyId}
                            className="supplier-card-item"
                            style={{
                              minWidth: 0 /* Prevent flexbox overflow issues */,
                            }}
                          >
                            <div className="card supplier-modern-card h-100 border-0">
                              <div className="card-logo-section text-center pt-4 pb-3">
                                <div className="logo-container mx-auto">
                                  {company.logoUrl ? (
                                    <img
                                      src={company.logoUrl}
                                      alt={company.companyName}
                                      className="supplier-logo-modern"
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                        const fallback =
                                          e.target.parentNode.querySelector(
                                            ".logo-fallback"
                                          );
                                        if (fallback)
                                          fallback.style.display = "flex";
                                      }}
                                    />
                                  ) : (
                                    <div className="logo-fallback d-flex align-items-center justify-content-center">
                                      <i className="ri-building-2-line"></i>
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="card-body text-center pt-0">
                                <h6 className="company-name mb-1 fw-bold text-dark">
                                  {company.companyName}
                                </h6>

                                <div className="contact-info mb-3">
                                  {company.email && (
                                    <div className="contact-item d-flex align-items-center justify-content-center mb-2">
                                      <i className="ri-mail-line text-primary me-2 fs-14"></i>
                                      <small className="text-truncate">
                                        {company.email}
                                      </small>
                                    </div>
                                  )}
                                  {company.phoneNumber && (
                                    <div className="contact-item d-flex align-items-center justify-content-center mb-2">
                                      <i className="ri-phone-line text-primary me-2 fs-14"></i>
                                      <small>{company.phoneNumber}</small>
                                    </div>
                                  )}
                                </div>

                                <div className="d-flex gap-2">
                                  {/* Show API Keys button for active companies with isSupplierAPI: true */}
                                  {company.isActive && company.isSupplierAPI ? (
                                    <button
                                      className="btn btn-primary btn-hover-effect flex-fill"
                                      onClick={() => handleKeyClick(company)}
                                      title="Manage API Details"
                                    >
                                      <i className="ri-key-2-line me-2"></i>
                                      API Keys
                                    </button>
                                  ) : (
                                    // Show Active/Inactive status with same button style but red text for inactive
                                    <button
                                      className={`btn flex-fill ${
                                        company.isActive
                                          ? "btn-success"
                                          : "btn-outline-danger"
                                      } btn-hover-effect`}
                                      style={{ cursor: "default" }}
                                      title={
                                        company.isActive
                                          ? "Active Company"
                                          : "Inactive Company"
                                      }
                                    >
                                      <i
                                        className={`ri-${
                                          company.isActive ? "check" : "close"
                                        }-line me-2`}
                                      ></i>
                                      {company.isActive ? "Active" : "Inactive"}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={{ gridColumn: "1 / -1" }}>
                          <div className="card">
                            <div className="card-body text-center py-5">
                              <i className="ri-building-2-line display-4 text-muted"></i>
                              <h5 className="mt-3">
                                No Supplier Companies Found
                              </h5>
                              <p className="text-muted">
                                There are no supplier companies available at the
                                moment.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* UPDATED API Details Modal - Single supplier type with dropdown */}
        <Modal show={apiShow} onHide={handleAPIClose} size="xl" scrollable>
          <Modal.Header closeButton>
            <Modal.Title>
              Supplier API Details - {selectedCompany?.companyName}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Info Alert */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="alert alert-info mb-0">
                  <i className="ri-information-line me-1"></i>
                  Managing API details for:{" "}
                  <strong>{selectedCompany?.companyName}</strong>
                </div>
              </div>
            </div>

            {loadingSuppliers ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading supplier APIs...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} autoComplete="off" noValidate>
                {renderSupplierForm()}

                <Modal.Footer>
                  <div className="hstack gap-2 justify-content-end mb-3 mt-4">
                    {btnLoading === false ? (
                      <button type="submit" className="btn btn-primary">
                        Save {selectedSupplierType} API
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled
                      >
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleAPIClose}
                    >
                      Close
                    </button>
                  </div>
                </Modal.Footer>
              </form>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default Supplier;
